/**
 * Lead Intake Validation
 * 
 * Validates and normalizes incoming lead payloads.
 * This is the gatekeeper before leads enter the routing system.
 * 
 * Supports two levels of validation:
 * - HARD failures: Discard the lead (missing required fields, invalid format)
 * - SOFT failures: Flag for review (unknown lead type with semantic match, minor mismatches)
 */

import { z } from 'zod';
import type {
  IncomingLeadPayload,
  NormalizedLead,
  IntakeValidationError,
  LeadTypeMapping,
} from './leadTypes';
import { findLeadTypeMapping, DEFAULT_LEAD_TYPE_MAPPINGS } from './leadTypes';

// --- Validation Severity ---

export type ValidationSeverity = 'HARD' | 'SOFT';

export interface ClassifiedValidationError extends IntakeValidationError {
  severity: ValidationSeverity;
  suggestedMatch?: string; // For SOFT failures with semantic matches
}

// --- Validation Schema ---

const phoneSchema = z.string()
  .min(10, 'Phone number must be at least 10 digits')
  .max(20, 'Phone number must be at most 20 characters')
  .transform(val => val.replace(/\D/g, '')) // Remove non-digits
  .refine(val => val.length >= 10, 'Phone number must have at least 10 digits');

const stateSchema = z.string()
  .min(2, 'State must be 2 characters')
  .max(2, 'State must be 2 characters')
  .transform(val => val.toUpperCase())
  .refine(val => VALID_STATES.has(val), 'Invalid US state code');

const emailSchema = z.string()
  .email('Invalid email format')
  .max(255, 'Email must be less than 255 characters')
  .optional()
  .nullable()
  .transform(val => val ?? undefined);

const nameSchema = z.string()
  .min(1, 'Name is required')
  .max(100, 'Name must be less than 100 characters')
  .transform(val => val.trim());

const leadTypeSchema = z.string()
  .min(1, 'Lead type is required')
  .max(100, 'Lead type must be less than 100 characters')
  .transform(val => val.trim());

// --- Valid States ---

const VALID_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
]);

// --- Validation Result ---

export interface ValidationResult {
  valid: boolean;
  errors: IntakeValidationError[];
  normalizedLead?: NormalizedLead;
  leadTypeMapping?: LeadTypeMapping;
}

export interface ClassifiedValidationResult {
  outcome: 'VALID' | 'SOFT_FAILURE' | 'HARD_FAILURE';
  errors: ClassifiedValidationError[];
  normalizedLead?: NormalizedLead;
  leadTypeMapping?: LeadTypeMapping;
  /** For SOFT failures, a best-guess normalized lead for review */
  partialLead?: Partial<NormalizedLead>;
}

// --- Main Validation Function ---

/**
 * Validate and normalize an incoming lead payload.
 */
export function validateLeadPayload(
  payload: IncomingLeadPayload,
  options: {
    generateId?: () => string;
    defaultSource?: string;
  } = {}
): ValidationResult {
  const errors: IntakeValidationError[] = [];
  const now = Date.now();
  
  const {
    generateId = () => `lead_${now}_${Math.random().toString(36).slice(2, 11)}`,
    defaultSource = 'unknown',
  } = options;
  
  // --- Validate phone ---
  let phone: string = '';
  try {
    phone = phoneSchema.parse(payload.phone);
  } catch (err) {
    if (err instanceof z.ZodError) {
      errors.push({
        field: 'phone',
        message: err.errors[0]?.message || 'Invalid phone number',
        code: 'INVALID_PHONE',
      });
    }
  }
  
  // --- Validate state ---
  let state: string = '';
  try {
    state = stateSchema.parse(payload.state);
  } catch (err) {
    if (err instanceof z.ZodError) {
      errors.push({
        field: 'state',
        message: err.errors[0]?.message || 'Invalid state code',
        code: 'INVALID_STATE',
      });
    }
  }
  
  // --- Validate email (optional) ---
  let email: string | undefined;
  if (payload.email) {
    try {
      email = emailSchema.parse(payload.email);
    } catch (err) {
      if (err instanceof z.ZodError) {
        errors.push({
          field: 'email',
          message: err.errors[0]?.message || 'Invalid email',
          code: 'INVALID_EMAIL',
        });
      }
    }
  }
  
  // --- Validate name ---
  let name: string = '';
  const rawName = payload.name || 
    (payload.firstName && payload.lastName 
      ? `${payload.firstName} ${payload.lastName}` 
      : payload.firstName || payload.lastName);
  
  if (rawName) {
    try {
      name = nameSchema.parse(rawName);
    } catch (err) {
      if (err instanceof z.ZodError) {
        errors.push({
          field: 'name',
          message: err.errors[0]?.message || 'Invalid name',
          code: 'INVALID_NAME',
        });
      }
    }
  } else {
    errors.push({
      field: 'name',
      message: 'Name is required (provide name, firstName, or lastName)',
      code: 'MISSING_NAME',
    });
  }
  
  // --- Validate lead type ---
  let leadType: string = '';
  let leadTypeMapping: LeadTypeMapping | null = null;
  
  if (payload.leadType) {
    try {
      leadType = leadTypeSchema.parse(payload.leadType);
      leadTypeMapping = findLeadTypeMapping(leadType);
      
      if (!leadTypeMapping) {
        errors.push({
          field: 'leadType',
          message: `Unknown lead type: ${leadType}. Valid types: ${DEFAULT_LEAD_TYPE_MAPPINGS.map(m => m.name).join(', ')}`,
          code: 'UNKNOWN_LEAD_TYPE',
        });
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        errors.push({
          field: 'leadType',
          message: err.errors[0]?.message || 'Invalid lead type',
          code: 'INVALID_LEAD_TYPE',
        });
      }
    }
  } else {
    errors.push({
      field: 'leadType',
      message: 'Lead type is required',
      code: 'MISSING_LEAD_TYPE',
    });
  }
  
  // --- Return validation result ---
  if (errors.length > 0 || !leadTypeMapping) {
    return { valid: false, errors };
  }
  
  // Build normalized lead
  const normalizedLead: NormalizedLead = {
    id: generateId(),
    name,
    phone,
    email,
    state,
    leadTypeId: leadTypeMapping.id,
    leadTypeName: leadTypeMapping.name,
    vertical: leadTypeMapping.vertical,
    estimatedValue: calculateEstimatedValue(leadTypeMapping, payload, state),
    source: payload.source || defaultSource,
    campaign: payload.campaign,
    subId: payload.subId,
    externalId: payload.externalId,
    receivedAt: payload.receivedAt || now,
    validatedAt: now,
    sourcePayload: payload,
  };
  
  return {
    valid: true,
    errors: [],
    normalizedLead,
    leadTypeMapping,
  };
}

// --- Value Estimation ---

/**
 * Calculate estimated value based on lead type, state, and qualification data.
 */
function calculateEstimatedValue(
  mapping: LeadTypeMapping,
  payload: IncomingLeadPayload,
  state: string
): number {
  let value = mapping.baseValue;
  
  // State-based adjustments
  const premiumStates = ['CA', 'NY', 'TX', 'FL', 'PA'];
  if (premiumStates.includes(state)) {
    value *= 1.1; // 10% premium for high-demand states
  }
  
  // Age-based adjustments for life insurance
  if (mapping.vertical === 'life_insurance' && payload.age) {
    if (payload.age >= 45 && payload.age <= 65) {
      value *= 1.05; // Prime age range for final expense
    } else if (payload.age < 35) {
      value *= 1.1; // Younger = longer policy term potential
    }
  }
  
  // Coverage amount adjustments
  if (payload.coverageAmount) {
    if (payload.coverageAmount >= 100000) {
      value *= 1.15;
    } else if (payload.coverageAmount >= 50000) {
      value *= 1.05;
    }
  }
  
  return Math.round(value * 100) / 100;
}

// --- Semantic Matching ---

/**
 * Find a close semantic match for an unknown lead type.
 * Returns the best matching type if similarity is above threshold.
 */
function findSemanticMatch(leadType: string): LeadTypeMapping | null {
  const normalized = leadType.toLowerCase().trim();
  
  // Patterns that suggest specific lead types
  const semanticPatterns: Array<{ pattern: RegExp; typeId: string }> = [
    // Final expense variations
    { pattern: /final\s*exp/i, typeId: 'life_fex' },
    { pattern: /burial/i, typeId: 'life_fex' },
    { pattern: /funeral/i, typeId: 'life_fex' },
    // Mortgage protection
    { pattern: /mortgage\s*prot/i, typeId: 'life_mp' },
    { pattern: /mp\s*insurance/i, typeId: 'life_mp' },
    // IUL variations
    { pattern: /indexed\s*universal/i, typeId: 'life_iul' },
    { pattern: /i\.?u\.?l\.?/i, typeId: 'life_iul' },
    // Mortgage types
    { pattern: /refi|refinanc|cash\s*out|rate\s*&\s*term/i, typeId: 'mortgage_refi' },
    { pattern: /purchase|home\s*buy/i, typeId: 'mortgage_purchase' },
    { pattern: /heloc|equity\s*line/i, typeId: 'mortgage_heloc' },
    { pattern: /reverse/i, typeId: 'mortgage_reverse' },
  ];
  
  for (const { pattern, typeId } of semanticPatterns) {
    if (pattern.test(normalized)) {
      return DEFAULT_LEAD_TYPE_MAPPINGS.find(m => m.id === typeId) || null;
    }
  }
  
  return null;
}

// --- Classified Validation Function ---

/**
 * Validate a lead payload with HARD/SOFT failure classification.
 * 
 * HARD failures: Missing required fields, invalid state/phone/email
 * SOFT failures: Unknown lead type with semantic match
 */
export function validateLeadPayloadClassified(
  payload: IncomingLeadPayload,
  options: {
    generateId?: () => string;
    defaultSource?: string;
  } = {}
): ClassifiedValidationResult {
  const errors: ClassifiedValidationError[] = [];
  const now = Date.now();
  
  const {
    generateId = () => `lead_${now}_${Math.random().toString(36).slice(2, 11)}`,
    defaultSource = 'unknown',
  } = options;
  
  // --- Validate phone (HARD) ---
  let phone: string = '';
  try {
    phone = phoneSchema.parse(payload.phone);
  } catch (err) {
    if (err instanceof z.ZodError) {
      errors.push({
        field: 'phone',
        message: err.errors[0]?.message || 'Invalid phone number',
        code: 'INVALID_PHONE',
        severity: 'HARD',
      });
    }
  }
  
  // --- Validate state (HARD) ---
  let state: string = '';
  try {
    state = stateSchema.parse(payload.state);
  } catch (err) {
    if (err instanceof z.ZodError) {
      errors.push({
        field: 'state',
        message: err.errors[0]?.message || 'Invalid state code',
        code: 'INVALID_STATE',
        severity: 'HARD',
      });
    }
  }
  
  // --- Validate email (HARD if provided and invalid) ---
  let email: string | undefined;
  if (payload.email) {
    try {
      email = emailSchema.parse(payload.email);
    } catch (err) {
      if (err instanceof z.ZodError) {
        errors.push({
          field: 'email',
          message: err.errors[0]?.message || 'Invalid email',
          code: 'INVALID_EMAIL',
          severity: 'HARD',
        });
      }
    }
  }
  
  // --- Validate name (HARD) ---
  let name: string = '';
  const rawName = payload.name || 
    (payload.firstName && payload.lastName 
      ? `${payload.firstName} ${payload.lastName}` 
      : payload.firstName || payload.lastName);
  
  if (rawName) {
    try {
      name = nameSchema.parse(rawName);
    } catch (err) {
      if (err instanceof z.ZodError) {
        errors.push({
          field: 'name',
          message: err.errors[0]?.message || 'Invalid name',
          code: 'INVALID_NAME',
          severity: 'HARD',
        });
      }
    }
  } else {
    errors.push({
      field: 'name',
      message: 'Name is required (provide name, firstName, or lastName)',
      code: 'MISSING_NAME',
      severity: 'HARD',
    });
  }
  
  // --- Validate lead type (can be SOFT) ---
  let leadType: string = '';
  let leadTypeMapping: LeadTypeMapping | null = null;
  let suggestedMapping: LeadTypeMapping | null = null;
  
  if (payload.leadType) {
    try {
      leadType = leadTypeSchema.parse(payload.leadType);
      leadTypeMapping = findLeadTypeMapping(leadType);
      
      if (!leadTypeMapping) {
        // Try semantic matching
        suggestedMapping = findSemanticMatch(leadType);
        
        if (suggestedMapping) {
          // SOFT failure - we have a suggestion
          errors.push({
            field: 'leadType',
            message: `Unknown lead type "${leadType}" - possible match: ${suggestedMapping.name}`,
            code: 'UNKNOWN_LEAD_TYPE_SOFT',
            severity: 'SOFT',
            suggestedMatch: suggestedMapping.name,
          });
        } else {
          // HARD failure - no semantic match
          errors.push({
            field: 'leadType',
            message: `Unknown lead type: ${leadType}. Valid types: ${DEFAULT_LEAD_TYPE_MAPPINGS.map(m => m.name).join(', ')}`,
            code: 'UNKNOWN_LEAD_TYPE',
            severity: 'HARD',
          });
        }
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        errors.push({
          field: 'leadType',
          message: err.errors[0]?.message || 'Invalid lead type',
          code: 'INVALID_LEAD_TYPE',
          severity: 'HARD',
        });
      }
    }
  } else {
    errors.push({
      field: 'leadType',
      message: 'Lead type is required',
      code: 'MISSING_LEAD_TYPE',
      severity: 'HARD',
    });
  }
  
  // --- Classify outcome ---
  const hardErrors = errors.filter(e => e.severity === 'HARD');
  const softErrors = errors.filter(e => e.severity === 'SOFT');
  
  // If any HARD errors, fail immediately
  if (hardErrors.length > 0) {
    return { outcome: 'HARD_FAILURE', errors };
  }
  
  // If only SOFT errors, return for review with partial lead
  if (softErrors.length > 0) {
    const effectiveMapping = suggestedMapping || leadTypeMapping;
    
    // Build partial lead for review
    const partialLead: Partial<NormalizedLead> = {
      id: generateId(),
      name: name || undefined,
      phone: phone || undefined,
      email,
      state: state || undefined,
      leadTypeId: effectiveMapping?.id || 'unknown',
      leadTypeName: effectiveMapping?.name || leadType,
      vertical: effectiveMapping?.vertical || 'life_insurance',
      estimatedValue: effectiveMapping 
        ? calculateEstimatedValue(effectiveMapping, payload, state)
        : 50, // Default value
      source: payload.source || defaultSource,
      campaign: payload.campaign,
      subId: payload.subId,
      externalId: payload.externalId,
      receivedAt: payload.receivedAt || now,
      validatedAt: now,
    };
    
    return { 
      outcome: 'SOFT_FAILURE', 
      errors,
      partialLead,
      leadTypeMapping: effectiveMapping || undefined,
    };
  }
  
  // No errors - fully valid
  const normalizedLead: NormalizedLead = {
    id: generateId(),
    name,
    phone,
    email,
    state,
    leadTypeId: leadTypeMapping!.id,
    leadTypeName: leadTypeMapping!.name,
    vertical: leadTypeMapping!.vertical,
    estimatedValue: calculateEstimatedValue(leadTypeMapping!, payload, state),
    source: payload.source || defaultSource,
    campaign: payload.campaign,
    subId: payload.subId,
    externalId: payload.externalId,
    receivedAt: payload.receivedAt || now,
    validatedAt: now,
  };
  
  return {
    outcome: 'VALID',
    errors: [],
    normalizedLead,
    leadTypeMapping: leadTypeMapping!,
  };
}
